import { toast } from 'react-toastify';
import {
  changePassword as requestPasswordChange,
  forgotPassword as requestPasswordResetEmailClient,
  isApiError,
  signIn,
  validateURL,
  validateUser,
} from 'src/services/client';
import { ErrorMessages, SuccessMessages } from 'src/common/constants';
import { ToastFail, ToastSuccess } from 'src/helpers/validationHelper';

export type ResetLinkValidation =
  | { status: 'valid' }
  | { status: 'invalid'; message: string };

export interface SubmitNewPasswordParams {
  email: string;
  code: string;
  password: string;
  newPassword: string;
  type: string;
}

export interface ChangePasswordParams {
  userId: string;
  email: string;
  oldPassword: string;
  newPassword: string;
}

export interface UseAuthFlowsAPIReturn {
  validateResetUrl: (email: string, code: string) => Promise<ResetLinkValidation>;
  submitNewPassword: (params: SubmitNewPasswordParams) => Promise<boolean>;
  changePassword: (params: ChangePasswordParams) => Promise<boolean>;
  requestPasswordResetEmail: (email: string) => Promise<boolean>;
}

const LINK_ALREADY_USED_MESSAGE: string = 'Link has already been used';

export const useAuthFlowsAPI = (): UseAuthFlowsAPIReturn => {
  const validateResetUrl = async (
    email: string,
    code: string
  ): Promise<ResetLinkValidation> => {
    try {
      const response: any = await validateURL(email, code);

      if (isApiError(response)) {
        ToastFail(ErrorMessages.error);
        return { status: 'invalid', message: '' };
      }

      const json: any = await response.json();

      if (json.statusCode === 200) {
        return { status: 'valid' };
      }

      if (json.statusCode === 401) {
        return { status: 'invalid', message: json.message };
      }

      if (json.statusCode === 300) {
        return { status: 'invalid', message: LINK_ALREADY_USED_MESSAGE };
      }

      if (json.statusCode === 404) {
        return { status: 'invalid', message: json.message };
      }

      ToastFail(ErrorMessages.error);
      return { status: 'invalid', message: '' };
    } catch (error) {
      console.error('Reset URL validation failed:', error);
      ToastFail(ErrorMessages.error);
      return { status: 'invalid', message: '' };
    }
  };

  const submitNewPassword = async (
    params: SubmitNewPasswordParams
  ): Promise<boolean> => {
    try {
      const response: any = await validateUser(params);

      if (isApiError(response)) {
        ToastFail(ErrorMessages.error);
        return false;
      }

      const json: any = await response.json();

      if (json.statusCode === 200) {
        ToastSuccess(SuccessMessages.passwordSuccess);
        return true;
      }

      if (json.statusCode === 404) {
        ToastFail(ErrorMessages.userNotFound);
        return false;
      }

      if (json.statusCode === 300) {
        ToastFail(ErrorMessages.userAlreadyActivated);
        return false;
      }

      if (json.statusCode === 401) {
        ToastFail(ErrorMessages.linkHasExpired);
        return false;
      }

      if (json.statusCode === 201) {
        ToastFail(json.message);
        return false;
      }

      ToastFail(ErrorMessages.passwordFail);
      return false;
    } catch (error) {
      console.error('Submit new password failed:', error);
      ToastFail(ErrorMessages.error);
      return false;
    }
  };

  const changePassword = async (
    params: ChangePasswordParams
  ): Promise<boolean> => {
    try {
      const verifyResponse: any = await signIn({
        Username: params.email,
        Password: params.oldPassword,
      });

      if (isApiError(verifyResponse)) {
        ToastFail(ErrorMessages.wrongPassword);
        return false;
      }

      const changeResponse: any = await requestPasswordChange({
        UserId: params.userId,
        Password: params.oldPassword,
        NewPassword: params.newPassword,
      });

      if (isApiError(changeResponse)) {
        toast.error('🚫 Password update failed.', {
          autoClose: 10000,
          position: 'top-center',
        });
        return false;
      }

      const json: any = await changeResponse.json();

      if (changeResponse.status === 200) {
        ToastSuccess(json);
        return true;
      }

      if (changeResponse.status === 201) {
        ToastFail(json);
        return false;
      }

      toast.error('🚫 Password update failed.', {
        autoClose: 10000,
        position: 'top-center',
      });
      return false;
    } catch (error) {
      console.error('Change password failed:', error);
      ToastFail(ErrorMessages.error);
      return false;
    }
  };

  const requestPasswordResetEmail = async (email: string): Promise<boolean> => {
    try {
      const response: any = await requestPasswordResetEmailClient(email);

      if (isApiError(response)) {
        ToastFail(ErrorMessages.error);
        return false;
      }

      if (response.ok || response.status === 200) {
        toast.success(
          'ⓘ A password reset link has been sent to your email.',
          { autoClose: 10000, position: 'top-center' }
        );
        return true;
      }

      if (response.status === 401) {
        const json: any = await response.json();
        ToastFail(json);
        return false;
      }

      if (response.status === 404) {
        ToastFail(
          'We could not find an account associated with the email you entered, please use a different email address and try again.'
        );
        return false;
      }

      ToastFail(ErrorMessages.error);
      return false;
    } catch (error) {
      console.error('Request password reset email failed:', error);
      ToastFail(ErrorMessages.error);
      return false;
    }
  };

  return {
    validateResetUrl,
    submitNewPassword,
    changePassword,
    requestPasswordResetEmail,
  };
};
